// Phase 4: CVC Words
// Real + Nonsense words by vowel (40% nonsense minimum)

import type { Phase } from "../../../../types/literacy"

export const phase4: Phase = {
  id: "phase-4",
  number: 4,
  title: "CVC Words",
  description: "Practice reading and blending CVC (Consonant-Vowel-Consonant) words",
  prerequisites: ["phase-3"],
  lessons: [
    {
      id: "lesson-4-1",
      phaseId: "phase-4",
      title: "Short A CVC Words",
      description: "Blend and read words with short A",
      drills: [
        {
          type: "blending",
          items: [],
          blendingWords: [
            { word: "cat", type: "real", pattern: "CVC" },
            { word: "bat", type: "real", pattern: "CVC" },
            { word: "sat", type: "real", pattern: "CVC" },
            { word: "mat", type: "real", pattern: "CVC" },
            { word: "rat", type: "real", pattern: "CVC" },
            { word: "hat", type: "real", pattern: "CVC" },
            { word: "fan", type: "real", pattern: "CVC" },
            { word: "can", type: "real", pattern: "CVC" },
            { word: "man", type: "real", pattern: "CVC" },
            { word: "pan", type: "real", pattern: "CVC" },
            { word: "tap", type: "real", pattern: "CVC" },
            { word: "map", type: "real", pattern: "CVC" },
            { word: "cap", type: "real", pattern: "CVC" },
            { word: "nap", type: "real", pattern: "CVC" },
            { word: "bag", type: "real", pattern: "CVC" },
            { word: "rag", type: "real", pattern: "CVC" },
            { word: "tag", type: "real", pattern: "CVC" },
            { word: "wag", type: "real", pattern: "CVC" },
            // Nonsense words (40%)
            { word: "dat", type: "nonsense", pattern: "CVC" },
            { word: "gat", type: "nonsense", pattern: "CVC" },
            { word: "lat", type: "nonsense", pattern: "CVC" },
            { word: "vat", type: "nonsense", pattern: "CVC" },
            { word: "zat", type: "nonsense", pattern: "CVC" },
            { word: "bam", type: "nonsense", pattern: "CVC" },
            { word: "fap", type: "nonsense", pattern: "CVC" },
            { word: "nar", type: "nonsense", pattern: "CVC" },
            { word: "tav", type: "nonsense", pattern: "CVC" },
            { word: "pag", type: "nonsense", pattern: "CVC" },
            { word: "zan", type: "nonsense", pattern: "CVC" },
            { word: "fad", type: "nonsense", pattern: "CVC" },
          ],
          masteryThreshold: 85,
          consecutiveCorrect: 3,
        },
      ],
    },
    {
      id: "lesson-4-2",
      phaseId: "phase-4",
      title: "Short I CVC Words",
      description: "Blend and read words with short I",
      drills: [
        {
          type: "blending",
          items: [],
          blendingWords: [
            { word: "sit", type: "real", pattern: "CVC" },
            { word: "bit", type: "real", pattern: "CVC" },
            { word: "hit", type: "real", pattern: "CVC" },
            { word: "pit", type: "real", pattern: "CVC" },
            { word: "fit", type: "real", pattern: "CVC" },
            { word: "kit", type: "real", pattern: "CVC" },
            { word: "lit", type: "real", pattern: "CVC" },
            { word: "pin", type: "real", pattern: "CVC" },
            { word: "fin", type: "real", pattern: "CVC" },
            { word: "win", type: "real", pattern: "CVC" },
            { word: "tin", type: "real", pattern: "CVC" },
            { word: "big", type: "real", pattern: "CVC" },
            { word: "dig", type: "real", pattern: "CVC" },
            { word: "fig", type: "real", pattern: "CVC" },
            { word: "pig", type: "real", pattern: "CVC" },
            { word: "wig", type: "real", pattern: "CVC" },
            // Nonsense words
            { word: "dit", type: "nonsense", pattern: "CVC" },
            { word: "git", type: "nonsense", pattern: "CVC" },
            { word: "mit", type: "nonsense", pattern: "CVC" },
            { word: "rit", type: "nonsense", pattern: "CVC" },
            { word: "zin", type: "nonsense", pattern: "CVC" },
            { word: "bin", type: "nonsense", pattern: "CVC" },
            { word: "gig", type: "nonsense", pattern: "CVC" },
            { word: "nig", type: "nonsense", pattern: "CVC" },
            { word: "vig", type: "nonsense", pattern: "CVC" },
            { word: "zit", type: "nonsense", pattern: "CVC" },
          ],
          masteryThreshold: 85,
          consecutiveCorrect: 3,
        },
      ],
      prerequisites: ["lesson-4-1"],
    },
    {
      id: "lesson-4-3",
      phaseId: "phase-4",
      title: "Short O CVC Words",
      description: "Blend and read words with short O",
      drills: [
        {
          type: "blending",
          items: [],
          blendingWords: [
            { word: "hot", type: "real", pattern: "CVC" },
            { word: "pot", type: "real", pattern: "CVC" },
            { word: "dot", type: "real", pattern: "CVC" },
            { word: "got", type: "real", pattern: "CVC" },
            { word: "lot", type: "real", pattern: "CVC" },
            { word: "not", type: "real", pattern: "CVC" },
            { word: "cot", type: "real", pattern: "CVC" },
            { word: "log", type: "real", pattern: "CVC" },
            { word: "dog", type: "real", pattern: "CVC" },
            { word: "fog", type: "real", pattern: "CVC" },
            { word: "hog", type: "real", pattern: "CVC" },
            { word: "jog", type: "real", pattern: "CVC" },
            { word: "hop", type: "real", pattern: "CVC" },
            { word: "top", type: "real", pattern: "CVC" },
            { word: "mop", type: "real", pattern: "CVC" },
            { word: "pop", type: "real", pattern: "CVC" },
            // Nonsense words
            { word: "bot", type: "nonsense", pattern: "CVC" },
            { word: "jot", type: "nonsense", pattern: "CVC" },
            { word: "zot", type: "nonsense", pattern: "CVC" },
            { word: "vot", type: "nonsense", pattern: "CVC" },
            { word: "nog", type: "nonsense", pattern: "CVC" },
            { word: "pog", type: "nonsense", pattern: "CVC" },
            { word: "rog", type: "nonsense", pattern: "CVC" },
            { word: "bop", type: "nonsense", pattern: "CVC" },
            { word: "fop", type: "nonsense", pattern: "CVC" },
            { word: "vop", type: "nonsense", pattern: "CVC" },
          ],
          masteryThreshold: 85,
          consecutiveCorrect: 3,
        },
      ],
      prerequisites: ["lesson-4-2"],
    },
    {
      id: "lesson-4-4",
      phaseId: "phase-4",
      title: "Short U CVC Words",
      description: "Blend and read words with short U",
      drills: [
        {
          type: "blending",
          items: [],
          blendingWords: [
            { word: "cup", type: "real", pattern: "CVC" },
            { word: "pup", type: "real", pattern: "CVC" },
            { word: "sun", type: "real", pattern: "CVC" },
            { word: "run", type: "real", pattern: "CVC" },
            { word: "fun", type: "real", pattern: "CVC" },
            { word: "bun", type: "real", pattern: "CVC" },
            { word: "gun", type: "real", pattern: "CVC" },
            { word: "cut", type: "real", pattern: "CVC" },
            { word: "but", type: "real", pattern: "CVC" },
            { word: "hut", type: "real", pattern: "CVC" },
            { word: "nut", type: "real", pattern: "CVC" },
            { word: "bug", type: "real", pattern: "CVC" },
            { word: "hug", type: "real", pattern: "CVC" },
            { word: "jug", type: "real", pattern: "CVC" },
            { word: "mug", type: "real", pattern: "CVC" },
            { word: "rug", type: "real", pattern: "CVC" },
            // Nonsense words
            { word: "bup", type: "nonsense", pattern: "CVC" },
            { word: "tun", type: "nonsense", pattern: "CVC" },
            { word: "zun", type: "nonsense", pattern: "CVC" },
            { word: "vun", type: "nonsense", pattern: "CVC" },
            { word: "dut", type: "nonsense", pattern: "CVC" },
            { word: "lut", type: "nonsense", pattern: "CVC" },
            { word: "tug", type: "nonsense", pattern: "CVC" },
            { word: "zug", type: "nonsense", pattern: "CVC" },
            { word: "pun", type: "nonsense", pattern: "CVC" },
            { word: "wut", type: "nonsense", pattern: "CVC" },
          ],
          masteryThreshold: 85,
          consecutiveCorrect: 3,
        },
      ],
      prerequisites: ["lesson-4-3"],
    },
    {
      id: "lesson-4-5",
      phaseId: "phase-4",
      title: "Short E CVC Words",
      description: "Blend and read words with short E",
      drills: [
        {
          type: "blending",
          items: [],
          blendingWords: [
            { word: "bed", type: "real", pattern: "CVC" },
            { word: "red", type: "real", pattern: "CVC" },
            { word: "fed", type: "real", pattern: "CVC" },
            { word: "led", type: "real", pattern: "CVC" },
            { word: "wed", type: "real", pattern: "CVC" },
            { word: "pen", type: "real", pattern: "CVC" },
            { word: "hen", type: "real", pattern: "CVC" },
            { word: "men", type: "real", pattern: "CVC" },
            { word: "ten", type: "real", pattern: "CVC" },
            { word: "den", type: "real", pattern: "CVC" },
            { word: "bet", type: "real", pattern: "CVC" },
            { word: "get", type: "real", pattern: "CVC" },
            { word: "let", type: "real", pattern: "CVC" },
            { word: "met", type: "real", pattern: "CVC" },
            { word: "net", type: "real", pattern: "CVC" },
            { word: "pet", type: "real", pattern: "CVC" },
            { word: "set", type: "real", pattern: "CVC" },
            { word: "wet", type: "real", pattern: "CVC" },
            // Nonsense words
            { word: "ped", type: "nonsense", pattern: "CVC" },
            { word: "ged", type: "nonsense", pattern: "CVC" },
            { word: "ned", type: "nonsense", pattern: "CVC" },
            { word: "zen", type: "nonsense", pattern: "CVC" },
            { word: "fen", type: "nonsense", pattern: "CVC" },
            { word: "ket", type: "nonsense", pattern: "CVC" },
            { word: "ret", type: "nonsense", pattern: "CVC" },
            { word: "tet", type: "nonsense", pattern: "CVC" },
            { word: "ved", type: "nonsense", pattern: "CVC" },
            { word: "zet", type: "nonsense", pattern: "CVC" },
          ],
          masteryThreshold: 85,
          consecutiveCorrect: 3,
        },
      ],
      prerequisites: ["lesson-4-4"],
    },
  ],
}
